/**
 * Sale Contract References — thin wrapper over the shared kho
 * `@/components/references`. Gọi chung 1 URL /references/{pageKey}; column-pref
 * localStorage. Page tự khai filter trong catalog (contract references không filter).
 */
import { useMemo, useState } from 'react'

import {
  REFERENCE_CATALOG,
  SALE_CONTRACT_REFERENCE_ORDER,
  type ReferencePageKey,
} from '@/components/references/catalog'
import ReferencesView from '@/components/references/ReferencesView'
import type { ReferenceMenuColumn } from '@/components/references/types'
import { useColumnWidths } from '@/hooks/useColumnWidths'
import { useLocalDatasheetColumns } from '@/utils/datasheetColumns/useLocalDatasheetColumns'
import { useGetSaleReferenceRowsQuery } from '@/store/api/saleContractApi'

export default function SaleReferencesView() {
  const [pageKey, setPageKey] = useState<ReferencePageKey>('value-updates')
  const [searchByPage, setSearchByPage] = useState<Partial<Record<ReferencePageKey, string>>>({})

  const page = REFERENCE_CATALOG[pageKey]
  const search = searchByPage[pageKey] ?? ''

  const queryArg = useMemo(() => ({ pageKey, limit: page.limit ?? 5000 }), [page.limit, pageKey])
  const { currentData, isFetching, error, refetch } = useGetSaleReferenceRowsQuery(queryArg, {
    refetchOnMountOrArgChange: true,
  })

  const menuColumns = useMemo<ReferenceMenuColumn[]>(
    () => page.columns.map((c) => ({ ...c, key: c.field, label: c.headerName })),
    [page.columns],
  )
  // Column layout keyed theo canonical page-key (dùng chung xuyên function):
  // cùng 1 page mở ở function nào cũng chung 1 layout width + ẩn/hiện, per-user.
  const columnsCtrl = useLocalDatasheetColumns({
    allColumns: menuColumns,
    storageKey: `references.${pageKey}`,
  })
  const widths = useColumnWidths(`references.${pageKey}`)

  const rows = currentData?.items ?? []
  return (
    <ReferencesView<ReferencePageKey>
      title="Sales Contract References"
      subtitle="Access switchboard parity for the 13 Sales Contract reference pages."
      pageOrder={SALE_CONTRACT_REFERENCE_ORDER}
      configs={REFERENCE_CATALOG}
      pageKey={pageKey}
      onPageKeyChange={setPageKey}
      rows={rows}
      search={search}
      onSearchChange={(value) => setSearchByPage((prev) => ({ ...prev, [pageKey]: value }))}
      loading={isFetching && rows.length === 0}
      fetching={isFetching && rows.length > 0}
      error={error}
      onRetry={() => {
        void refetch()
      }}
      columnsCtrl={columnsCtrl}
      widths={widths}
      totalsMode="sum-flag"
    />
  )
}
